import { ConfigProvider } from 'antd';
import { type i18n as I18n } from 'i18next';
import { Suspense } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { ThemeProvider } from 'styled-components';
import Bootstrap, { type BootstrapProps } from './Bootstrap';
import { AccountContextManager } from './components/contexts/AccountContextManager';
import { RouterContextManager } from './components/contexts/RouterContextManager';
import { ThemeController } from './components/contexts/ThemeController';
import LoadingElement from './components/LoadingElement';
import SystemMessageHandler from './components/SystemMessageHandler';
import HeaderContextManager from './layouts/HeaderContextManager';
import MainRouter from './routers/MainRouter';
import { RuntimeProvider, type RuntimeConfig } from './runtimeConfig';
import theme from './theme';
import ThemeComponentsProvider from './themes';
import useObserverRoot from './utilities/useObserveRoot';

export type AppProps = {
    i18n: I18n;
    runtime: RuntimeConfig;
    createApolloClient: BootstrapProps['createApolloClient'];
};

const AppThemeProvider = ({ children }: { children: JSX.Element | React.ReactNode }) => {
    const { t, i18n } = useTranslation(['core', 'antd']);
    const locale = i18n.getResourceBundle(i18n.language, 'antd');

    useObserverRoot();

    return (
        <ConfigProvider direction={t('core:orientation')} locale={locale}>
            <ThemeProvider theme={theme}>
                <ThemeController>
                    <ThemeComponentsProvider>{children}</ThemeComponentsProvider>
                </ThemeController>
            </ThemeProvider>
        </ConfigProvider>
    );
};

const Inner = () => (
    <Suspense fallback={<LoadingElement />}>
        <AccountContextManager>
            <RouterContextManager>
                <HeaderContextManager>
                    <SystemMessageHandler />
                    <MainRouter />
                </HeaderContextManager>
            </RouterContextManager>
        </AccountContextManager>
    </Suspense>
);

const App = ({ i18n, runtime, createApolloClient }: AppProps) => (
    <RuntimeProvider runtime={runtime}>
        <I18nextProvider i18n={i18n}>
            <AppThemeProvider>
                <Bootstrap createApolloClient={createApolloClient}>
                    <Inner />
                </Bootstrap>
            </AppThemeProvider>
        </I18nextProvider>
    </RuntimeProvider>
);

export default App;
